'use client';

import { merriweather } from '@/app/fonts';
import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { useCreateEducationMutation } from '@/api/mutations/createEducation';
import { useUpdateEducationMutation } from '@/api/mutations/updateEducation';
import { useEducationCategories } from '@/api/useEducationCategories';
import { useGetEducationById } from '@/api/useGetEducationById';
import axios from 'axios';
import { uploadImageWithThumbnail } from '@/utils/uploadImageWithThumbnail';
// Step Components
import OverviewStep from './steps/OverviewStep';
import RankingsAdmissionsStep from './steps/RankingsAdmissionsStep';
import CostStep from './steps/CostStep';
import AcademicsStep from './steps/AcademicsStep';
import StudentBodyStep from './steps/StudentBodyStep';
import { INPUT_LIMITS } from '@/constants/inputLimits';
import { useIsPublic } from '@/context/PublicMode';
import { useAuth } from '@/context/Auth';
import { useDraftPersistence } from '@/hooks/useDraftPersistence';
import { analytics } from '@/lib/analytics';

// Upload utility function
export async function uploadFilesToS3(files: File[]): Promise<string[]> {
  const urls: string[] = [];
  for (const file of files) {
    try {
      // Uploads the original AND its WebP thumbnail (thumbnails/education/…webp).
      const key = await uploadImageWithThumbnail(file, 'education');
      urls.push(key);
    } catch (err: any) {
      throw new Error(`Failed to upload ${file.name} ${err.message}`);
    }
  }
  return urls;
}

// Form validation schema
const createEducationSchema = z.object({
  // Overview Step
  name: z
    .string()
    .min(1, 'Name is required')
    .max(
      INPUT_LIMITS.TEXT.TITLE_MAX,
      `Name cannot exceed ${INPUT_LIMITS.TEXT.TITLE_MAX} characters`
    ),
  overview: z.string().min(1, 'Overview is required'),
  categoryID: z.string().min(1, 'Category is required'),
  neighborhoodID: z.string().min(1, 'Neighborhood is required'),
  thumbnailImage: z.string().optional(),
  coverImage: z.string().optional(),
  website: z
    .string()
    .transform((val) => val.trim())
    .optional()
    .or(z.literal('')),
  email: z
    .string()
    .email('Please enter a valid email')
    .optional()
    .or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  workHours: z.string().optional().or(z.literal('')),

  // Rankings & Admissions Step
  setting: z.string().optional(),
  enrollment: z.string().min(1, 'Enrollment is required'),
  acceptanceRate: z.string().optional().nullable(),
  agesEnrolled: z.string().min(1, 'Ages enrolled is required'),
  partOrFullTime: z
    .array(z.enum(['Part', 'Full']))
    .min(1, 'Please select at least one option'),
  applicationDeadline: z.string().optional(),
  applicationFee: z.string().optional().nullable(),
  studentFacultyRatio: z.string().min(1, 'Student-faculty ratio is required'),
  method: z.string().optional(),
  topSecondaries: z.string().optional(),

  // Cost Step
  fullTimeCost: z.string().optional(),
  fullTimeRegistrationCost: z.string().optional(),
  fullTimeSuppliesCost: z.string().optional(),
  partTimeCost: z.string().optional(),
  partTimeRegistrationCost: z.string().optional(),
  partTimeSuppliesCost: z.string().optional(),
  doesProvideGrants: z.boolean().optional(),
  doesProvideScholarships: z.boolean().optional(),
  // Academics Step
  curriculum: z.string().optional(),
  isReligious: z.boolean().optional(),
  specialties: z.string().optional(),
  // Student Body Step
  clubs: z.string().optional(),
  sports: z.string().optional(),
  neighborhoodCrimeInfo: z.string().optional(),
  campusSize: z.number().optional().nullable(),
  culture: z.string().min(1, 'Culture is required'),
  coordinates: z
    .array(z.number())
    .length(2)
    .optional()
    .or(z.literal(undefined)),
});

// Members never see the Rankings & Admissions step, so the two fields it makes
// required there must not be able to block their submit.
const memberEducationSchema = createEducationSchema.extend({
  enrollment: z.string().optional(),
  studentFacultyRatio: z.string().optional(),
});

type FormData = z.infer<typeof createEducationSchema>;

// Everything that only ever appears on a business-only step (plus the two
// Student Body fields members don't get). Dropped from the payload entirely for
// members so an edit can't blank out values they were never shown.
const BUSINESS_ONLY_FIELDS = [
  // Rankings & Admissions
  'setting',
  'enrollment',
  'acceptanceRate',
  'applicationDeadline',
  'applicationFee',
  'studentFacultyRatio',
  'method',
  'topSecondaries',
  // Academics
  'curriculum',
  'isReligious',
  'specialties',
  // Student Body
  'campusSize',
  'neighborhoodCrimeInfo',
] as const;

type EducationDraft = Omit<FormData, 'thumbnailImage' | 'coverImage'> & {
  selectedLocation: {
    lat: number;
    lng: number;
    formattedAddress?: string;
    country?: string;
    city?: string;
    street?: string;
  } | null;
  selectedNeighborhoodName: string;
};

interface CreateEducationDialogProps {
  categoryID?: string;
  triggerClassName?: string;
  btnName?:string;
  onSuccess?: (education: any) => void;
  educationId?: string; // For editing mode
  isEdit?: boolean; // Flag to indicate edit mode
  open?: boolean; // External control of dialog state
  onOpenChange?: (open: boolean) => void; // External control of dialog state
}

type StepKey =
  | 'overview'
  | 'rankings'
  | 'cost'
  | 'academics'
  | 'studentBody';

// Rankings & Admissions and Academics are business-only; members get a
// three-step form (Overview → Cost → Student Body).
const ALL_STEPS: {
  key: StepKey;
  label: string;
  businessOnly?: boolean;
}[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'rankings', label: 'Rankings & Admissions', businessOnly: true },
  { key: 'cost', label: 'Cost' },
  { key: 'academics', label: 'Academics', businessOnly: true },
  { key: 'studentBody', label: 'Student Body' },
];

// Step-level validation schemas
const stepValidationSchemas: Record<StepKey, z.ZodObject<any>> = {
  overview: z.object({
    name: z
      .string()
      .min(1, 'Name is required')
      .max(
        INPUT_LIMITS.TEXT.TITLE_MAX,
        `Name cannot exceed ${INPUT_LIMITS.TEXT.TITLE_MAX} characters`
      ),
    overview: z.string().min(1, 'Overview is required'),
    categoryID: z.string().min(1, 'Category is required'),
    agesEnrolled: z.string().min(1, 'Ages enrolled is required'),
    partOrFullTime: z
      .array(z.enum(['Part', 'Full']))
      .min(1, 'Please select at least one option'),
    neighborhoodID: z.string().min(1, 'Neighborhood is required'),
    thumbnailImage: z.string().optional(),
    coverImage: z.string().optional(),
    website: z
      .string()
      .transform((val) => val.trim())
      .optional()
      .or(z.literal('')),
    email: z
      .string()
      .email('Please enter a valid email')
      .optional()
      .or(z.literal('')),
    phone: z.string().optional().or(z.literal('')),
    workHours: z.string().optional().or(z.literal('')),
  }),
  rankings: z.object({
    setting: z.string().optional(),
    enrollment: z.string().min(1, 'Enrollment is required'),
    acceptanceRate: z.string().optional().nullable(),
    applicationDeadline: z.string().optional(),
    applicationFee: z.string().optional().nullable(),
    studentFacultyRatio: z.string().min(1, 'Student-faculty ratio is required'),
    method: z.string().optional(),
    topSecondaries: z.string().optional(),
  }),
  cost: z.object({
    fullTimeCost: z.string().optional(),
    fullTimeRegistrationCost: z.string().optional(),
    fullTimeSuppliesCost: z.string().optional(),
    partTimeCost: z.string().optional(),
    partTimeRegistrationCost: z.string().optional(),
    partTimeSuppliesCost: z.string().optional(),
    doesProvideGrants: z.boolean().optional(),
    doesProvideScholarships: z.boolean().optional(),
  }),
  academics: z.object({
    curriculum: z.string().optional(),
    specialties: z.string().optional(),
  }),
  studentBody: z.object({
    clubs: z.string().optional(),
    sports: z.string().optional(),
    neighborhoodCrimeInfo: z.string().optional(),
    campusSize: z.number().optional().nullable(),
    culture: z.string().min(1, 'Culture is required'),
  }),
};

export default function CreateEducationDialog({
  categoryID,
  triggerClassName = '',
  btnName,
  onSuccess,
  educationId,
  isEdit = false,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
}: CreateEducationDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);

  // Use external state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = externalOnOpenChange || setInternalOpen;
  const [currentStep, setCurrentStep] = useState(0);
  const [thumbnailFiles, setThumbnailFiles] = useState<File[]>([]);
  const [selectedNeighborhoodName, setSelectedNeighborhoodName] =
    useState<string>('');
  const [coverFiles, setCoverFiles] = useState<File[]>([]);
  const [showThumbnailImageErrors] =
    useState(false);
  const [showCoverImageErrors] = useState(false);
  const [hasInitializedFromExisting, setHasInitializedFromExisting] =
    useState<boolean>(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
    formattedAddress?: string;
    country?: string;
    city?: string;
    street?: string;
  } | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(false);
  const isPublic = useIsPublic();
  const { idTokenPayload } = useAuth();
  const roleIsBusiness = idTokenPayload?.['custom:role'] === '1';
  const steps = useMemo(
    () => ALL_STEPS.filter((step) => roleIsBusiness || !step.businessOnly),
    [roleIsBusiness]
  );
  const { data: categoriesData } = useEducationCategories();

  const createEducationMutation = useCreateEducationMutation();
  const updateEducationMutation = useUpdateEducationMutation(educationId || '');
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  // Fetch existing education data when in edit mode
  const { data: existingEducation, isLoading: isLoadingEducation } =
    useGetEducationById(educationId || '', {
      // Prevent React Query from overwriting in-progress edits on refocus
      refetchOnWindowFocus: false,
      // Data for a single education record is unlikely to change while the user edits
      // the form, so we can treat it as fresh for this session.
      staleTime: 5 * 60 * 1000,
    });
     function formatToE164(phone?: string) {
      if (!phone) return '';
    
      const parsed = parsePhoneNumberFromString(phone, 'US');
    
      return parsed?.format('E.164') || '';
    }
  const form = useForm<FormData>({
    resolver: zodResolver(
      (roleIsBusiness ? createEducationSchema : memberEducationSchema) as any
    ),
    mode: 'onSubmit', // Enable validation on change
    reValidateMode: 'onChange', // Re-validate on change
    defaultValues: {
      name: '',
      overview: '',
      categoryID: categoryID || '',
      neighborhoodID: '',
      thumbnailImage: '',
      coverImage: '',
      setting: '',
      enrollment: '',
      acceptanceRate: '',
      agesEnrolled: '',
      partOrFullTime: [],
      applicationDeadline: '',
      applicationFee: '',
      studentFacultyRatio: '',
      method: '',
      fullTimeCost: '',
      fullTimeRegistrationCost: '',
      fullTimeSuppliesCost: '',
      partTimeCost: '',
      partTimeRegistrationCost: '',
      partTimeSuppliesCost: '',
      doesProvideScholarships: false,
      doesProvideGrants: false,
      curriculum: '',
      isReligious: false,
      neighborhoodCrimeInfo: '',
      campusSize: undefined,
      culture: '',
      website: '',
      email: '',
      phone: '',
      workHours: '',
    },
  });
  // Populate form with existing education data when in edit mode.
  // Guarded so it only runs once per dialog open / educationId.
  useEffect(() => {
    if (!isEdit || !existingEducation?.responseObject || isLoadingEducation) {
      return;
    }

    // Never overwrite user edits once they have started typing
    if (hasInitializedFromExisting || form.formState.isDirty) {
      return;
    }

    const education = existingEducation.responseObject;

    if (education.neighborhood?.name) {
      setSelectedNeighborhoodName(education.neighborhood.name);
    }

    const coordinates = education.coordinates;
    if (
      coordinates &&
      Array.isArray(coordinates) &&
      coordinates.length >= 2 &&
      coordinates[0] !== 0 &&
      coordinates[1] !== 0
    ) {
      setSelectedLocation({
        lng: coordinates[0],
        lat: coordinates[1],
      });
    }

    form.reset({
      name: education.name || '',
      overview: education.overview || '',
      categoryID: education.categoryID || categoryID || '',
      neighborhoodID: education.neighborhoodID || '',
      thumbnailImage: '',
      coverImage: '',
      setting: education.setting || '',
      enrollment: education.enrollment ? String(education.enrollment) : '',
      acceptanceRate: education.acceptanceRate
        ? String(education.acceptanceRate)
        : '',
      agesEnrolled: education.agesEnrolled || '',
      partOrFullTime:
        education.partOrFullTime === 'Both'
          ? ['Part', 'Full']
          : education.partOrFullTime === 'Part Time'
            ? ['Part']
            : education.partOrFullTime === 'Full Time'
              ? ['Full']
              : [],
      applicationDeadline: education.applicationDeadline || '',
      applicationFee: education.applicationFee
        ? String(education.applicationFee)
        : '',
      studentFacultyRatio: education.studentFacultyRatio || '',
      method: education.method || '',
      fullTimeCost:
        education.fullTimeCost != null ? String(education.fullTimeCost) : '',
      fullTimeRegistrationCost:
        education.fullTimeRegistrationCost != null ? String(education.fullTimeRegistrationCost) : '',
      fullTimeSuppliesCost:
        education.fullTimeSuppliesCost != null ? String(education.fullTimeSuppliesCost) : '',
      partTimeCost:
        education.partTimeCost != null ? String(education.partTimeCost) : '',
      partTimeRegistrationCost:
        education.partTimeRegistrationCost != null ? String(education.partTimeRegistrationCost) : '',
      partTimeSuppliesCost:
        education.partTimeSuppliesCost != null ? String(education.partTimeSuppliesCost) : '',
      doesProvideScholarships: education.doesProvideScholarships === 'true',
      doesProvideGrants: education.doesProvideGrants === 'true',
      curriculum: education.curriculum || '',
      isReligious: education.isReligious || false,
      neighborhoodCrimeInfo: education.neighborhoodCrimeInfo || '',
      campusSize: education.campusSize || undefined,
      culture: education.culture || '',
      website: education.website || '',
      email: education.email || '',
      phone: formatToE164(education.phone),
      workHours: education.workHours || '',
      topSecondaries: education.topSecondaries?.join(',') || '',
      clubs: education.clubs?.join(',') || '',
      sports: education.sports?.join(',') || '',
      specialties: education.specialties?.join(',') || '',
    });

    // Set image fields with a delay to ensure FileUpload components don't interfere
    setTimeout(() => {
      form.setValue(
        'thumbnailImage',
        education.thumbnailImage ? 'existing' : ''
      );
      form.setValue('coverImage', education.coverImage ? 'existing' : '');
    }, 300);

    setHasInitializedFromExisting(true);
  }, [
    isEdit,
    existingEducation,
    form,
    categoryID,
    isLoadingEducation,
    educationId,
    hasInitializedFromExisting,
    form.formState.isDirty,
  ]);

  // Reset initialization flag when dialog closes or when switching records
  useEffect(() => {
    if (!open || !isEdit) {
      setHasInitializedFromExisting(false);
    }
  }, [open, isEdit, educationId]);

  // The role only resolves once the id token decodes, so the step list can
  // shrink under us — never leave the user parked past the last step.
  useEffect(() => {
    if (currentStep > steps.length - 1) {
      setCurrentStep(steps.length - 1);
    }
  }, [currentStep, steps.length]);

  // Scroll to top when step changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [currentStep]);

  const [isFormDirty, setIsFormDirty] = useState(false);
  useEffect(() => {
    const subscription = form.watch((values, { type }) => {
      if (type) {
        setIsFormDirty(true);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Drafts only apply to new listings — thumbnail/cover files can't be
  // serialized, so restoring a draft never brings images back with it.
  const { clearDraft } = useDraftPersistence<EducationDraft>({
    storageKey: 'pp_draft_v1_education_create',
    enabled: open && !isEdit,
    isSubmitting:
      loading ||
      createEducationMutation.isPending ||
      updateEducationMutation.isPending,
    getDraftData: () => {
      const values = form.getValues();
      const hasMeaningfulData =
        !!values.name?.trim() || !!values.overview?.trim();
      if (!hasMeaningfulData) return null;

      const { thumbnailImage, coverImage, ...rest } = values;
      return { ...rest, selectedLocation, selectedNeighborhoodName };
    },
    applyDraft: (draft) => {
      const {
        selectedLocation: draftLocation,
        selectedNeighborhoodName: draftNeighborhoodName,
        ...formValues
      } = draft;
      (Object.keys(formValues) as (keyof typeof formValues)[]).forEach(
        (key) => {
          form.setValue(key as any, formValues[key] as any);
        }
      );
      if (draftLocation) setSelectedLocation(draftLocation);
      if (draftNeighborhoodName)
        setSelectedNeighborhoodName(draftNeighborhoodName);
      setIsFormDirty(true);
    },
  });

  // Keep image states in sync with form
  useEffect(() => {
    if (isEdit) return;
    form.setValue('thumbnailImage', thumbnailFiles.length > 0 ? 'new' : '');
    form.setValue('coverImage', coverFiles.length > 0 ? 'new' : '');
  }, [thumbnailFiles, coverFiles, form, isEdit]);

  const handleThumbnailChange = useCallback((files: File[]) => {
    setThumbnailFiles(files);
  }, []);

  const handleCoverChange = useCallback((files: File[]) => {
    setCoverFiles(files);
  }, []);

  // Get current category from URL
  /**
   * 
  const getCurrentCategory = () => {
    if (!categoriesData?.responseObject?.rows) return null;

    // Extract category ID from URL path like /education/[categoryID]/...
    const pathParts = pathname.split('/');
    const educationIndex = pathParts.findIndex((part) => part === 'education');
    if (educationIndex !== -1 && pathParts[educationIndex + 1]) {
      const categoryId = pathParts[educationIndex + 1];
      return categoriesData.responseObject.rows.find(
        (cat: any) => cat.id === categoryId
      );
    }
    return null;
  };
   */

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && isFormDirty) {
      setConfirmCloseOpen(true);
    } else {
      setOpen(isOpen);
    }
  };

  const handleConfirmDiscard = () => {
    setConfirmCloseOpen(false);
    setIsFormDirty(false);
    if (!isEdit) clearDraft();
    setOpen(false);
    handleClose?.();
  };

  const handleCancelDiscard = () => {
    setConfirmCloseOpen(false);
  };

  // The heartbeat in useDraftPersistence already saved within the last 2s —
  // this just closes without clearing what's already in storage.
  const handleSaveDraftAndClose = () => {
    setConfirmCloseOpen(false);
    setIsFormDirty(false);
    setOpen(false);
    handleClose?.();
  };

  const handleNext = async () => {
    const currentStepKey = steps[currentStep]?.key;
    if (!currentStepKey) return;
    const currentStepFields = stepValidationSchemas[currentStepKey].shape
      ? Object.keys(stepValidationSchemas[currentStepKey].shape)
      : [];
    const isValid = await form.trigger(currentStepFields as (keyof FormData)[]);

    if (!isValid) return;
    // --- Custom validation for Cost Step based on Overview choice ---
    if (currentStepKey === 'cost') {
      const partOrFullTime = form.getValues('partOrFullTime') || [];

      let hasError = false;

      // Only the rate is required for a selected mode. Registration and
      // supplies are optional: most schools have no figure for them (they're
      // null on the records), and requiring one meant an admin couldn't save
      // any edit to such a school without inventing numbers.
      if (partOrFullTime.includes('Full') && !form.getValues('fullTimeCost')) {
        form.setError('fullTimeCost', {
          message: 'Full time cost is required',
        });
        hasError = true;
      }
      if (partOrFullTime.includes('Part') && !form.getValues('partTimeCost')) {
        form.setError('partTimeCost', {
          message: 'Part time cost is required',
        });
        hasError = true;
      }

      if (hasError) return; // stop going to next step
    }

    form.clearErrors();
    if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentStep(0);

    // Reset form to default values
    form.reset({
      name: '',
      overview: '',
      categoryID: categoryID || '',
      neighborhoodID: '',
      thumbnailImage: '',
      coverImage: '',
      setting: '',
      enrollment: '',
      acceptanceRate: '',
      agesEnrolled: '',
      partOrFullTime: [],
      applicationDeadline: '',
      applicationFee: '',
      studentFacultyRatio: '',
      method: '',
      topSecondaries: '',
      fullTimeCost: '',
      fullTimeRegistrationCost: '',
      fullTimeSuppliesCost: '',
      partTimeCost: '',
      partTimeRegistrationCost: '',
      partTimeSuppliesCost: '',
      doesProvideScholarships: false,
      doesProvideGrants: false,
      curriculum: '',
      isReligious: false,
      specialties: '',
      clubs: '',
      email: '',
      phone: '',
      workHours: '',
      sports: '',
      neighborhoodCrimeInfo: '',
      campusSize: undefined,
      culture: '',
      website: '',
    });

    // Reset file states
    setThumbnailFiles([]);
    setCoverFiles([]);
    setSelectedLocation(null);
    setShowLocationPicker(false);
  };
  const onSubmit = async (data: FormData) => {
    try {
      setLoading(true);
      const {
        thumbnailImage: _thumbnailImage,
        coverImage: _coverImage,
        website: _website,
        ...restData
      } = data;
      // Handle file uploads using the same system as marketplace
      let thumbnailImageUrl = '';
      let coverImageUrl = '';

      if (thumbnailFiles.length > 0) {
        const uploadedUrls = await uploadFilesToS3(thumbnailFiles);
        thumbnailImageUrl = uploadedUrls[0] || '';
      }

      if (coverFiles.length > 0) {
        const uploadedUrls = await uploadFilesToS3(coverFiles);
        coverImageUrl = uploadedUrls[0] || '';
      }

      const resolvedThumbnailImage =
        thumbnailImageUrl ||
        (isEdit ? existingEducation?.responseObject?.thumbnailImage || '' : '');
      const resolvedCoverImage =
        coverImageUrl ||
        (isEdit ? existingEducation?.responseObject?.coverImage || '' : '');
      const trimmedWebsite = data.website?.trim();

      const educationData: Record<string, any> = {
        ...restData,
        // Handle image URLs - preserve existing images in edit mode if no new files uploaded
        ...(resolvedThumbnailImage && { thumbnailImage: resolvedThumbnailImage }),
        ...(resolvedCoverImage && { coverImage: resolvedCoverImage }),
        doesProvideGrants: String(data.doesProvideGrants),
        doesProvideScholarships: String(data.doesProvideScholarships),
        neighborhoodCrimeInfo: data.neighborhoodCrimeInfo || '',
        topSecondaries:
          data.topSecondaries?.split(',').map((item) => item.trim()) || [],
        clubs: data.clubs?.split(',').map((item) => item.trim()),
        sports: data.sports?.split(',').map((item) => item.trim()),
        specialties: data.specialties?.split(',').map((item) => item.trim()),
        ...(trimmedWebsite && { website: trimmedWebsite }),
        partOrFullTime:
          data.partOrFullTime?.length === 2
            ? 'Both'
            : data.partOrFullTime?.[0] + ' Time',
        ...(selectedLocation && {
          coordinates: [selectedLocation.lng, selectedLocation.lat],
        }),
        // Arrays
      };

      // Members never fill these in, so send nothing rather than blanks.
      if (!roleIsBusiness) {
        BUSINESS_ONLY_FIELDS.forEach((field) => {
          delete educationData[field];
        });
      }

      let result;
      if (isEdit && educationId) {
        // Update existing education
        await updateEducationMutation.mutateAsync(educationData as any);
        toast.success('Education updated successfully!');
      } else {
        // Create new education
        result = await createEducationMutation.mutateAsync(
          educationData as any
        );
        analytics.educationCreated({ category_id: data.categoryID });
        toast.success('Education created successfully!');
        clearDraft();
      }

      onSuccess?.(result?.responseObject);
      handleClose();
    } catch (err: any) {
      let errorMessage = 'Something went wrong';
      if (axios.isAxiosError(err) && err.response) {
        const data = err.response.data;
        if (data?.responseObject && Array.isArray(data.responseObject)) {
          errorMessage = data.responseObject
            .map((e: any) => e.message)
            .join(', ');
        } else if (data?.message) {
          errorMessage = data.message;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    const stepProps = {
      control: form.control,
      errors: form.formState.errors,
    };

    const selectedCategoryName = categoriesData?.responseObject?.rows?.find(
      (opt: any) => opt.id === form.getValues('categoryID')
    )?.name;
    switch (steps[currentStep]?.key) {
      case 'overview':
        return (
          <OverviewStep
            {...stepProps}
            thumbnailFiles={thumbnailFiles}
            coverFiles={coverFiles}
            showThumbnailImageErrors={showThumbnailImageErrors}
            showCoverImageErrors={showCoverImageErrors}
            onThumbnailChange={handleThumbnailChange}
            onCoverChange={handleCoverChange}
            categoriesData={categoriesData}
            existingThumbnailImage={
              existingEducation?.responseObject?.thumbnailImage || undefined
            }
            existingCoverImage={
              existingEducation?.responseObject?.coverImage || undefined
            }
            isEdit={isEdit}
            selectedNeighborhoodName={selectedNeighborhoodName}
            setSelectedNeighborhoodName={setSelectedNeighborhoodName}
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            showLocationPicker={showLocationPicker}
            setShowLocationPicker={setShowLocationPicker}
          />
        );

      case 'rankings':
        return (
          <RankingsAdmissionsStep
            {...stepProps}
            errors={form.formState.errors}
            isEdit={isEdit}
            selectedCategoryName={selectedCategoryName}
          />
        );

      case 'cost':
        return <CostStep {...stepProps} isEdit={isEdit} />;

      case 'academics':
        return (
          <AcademicsStep
            {...stepProps}
            errors={form.formState.errors}
            isEdit={isEdit}
          />
        );

      case 'studentBody':
        return (
          <StudentBodyStep
            {...stepProps}
            errors={form.formState.errors}
            isEdit={isEdit}
            isBusiness={roleIsBusiness}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <AlertDialog open={confirmCloseOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isEdit ? 'Discard changes?' : 'Save as draft?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isEdit
                ? 'You have unsaved changes. Are you sure you want to discard them?'
                : 'Do you want to save your changes as a draft so you can come back to them later?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDiscard}>
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDiscard}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isEdit ? 'Discard' : "Don't save"}
            </AlertDialogAction>
            {!isEdit && (
              <AlertDialogAction onClick={handleSaveDraftAndClose}>
                Save draft
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={open && !isPublic} onOpenChange={handleOpenChange}>
        {externalOpen === undefined && (
          <DialogTrigger asChild>
            <Button
              className={`${merriweather.className} 
          ${triggerClassName} rounded-full font-bold text-md w-full py-2 px-3`}
            >
              {btnName || 'POST'}
            </Button>
          </DialogTrigger>
        )}
        <DialogContent className="w-[calc(100vw-2rem)] rounded-4xl max-w-6xl sm:max-w-4xl max-h-[90vh] md:max-h-[85%] flex flex-col ">
          {/* Fixed Header */}
          <div className="flex-shrink-0 p-4 md:p-6">
            <DialogHeader className="md:w-full">
              <DialogTitle>
                {isEdit ? 'Edit Education' : 'Create New Education'}
              </DialogTitle>
            </DialogHeader>

            {/* Step Indicator */}
            <p className="md:hidden text-center text-sm font-medium text-black mt-3">
              Step {currentStep + 1} of {steps.length}:{' '}
              {steps[currentStep]?.label}
            </p>
            <div className="flex items-center justify-center mt-4">
              <div className="flex items-center space-x-2">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className={`flex items-center  ${
                      index <= currentStep ? 'text-black' : 'text-gray-400'
                    }`}
                  >
                    <div
                      className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-medium ${
                        index <= currentStep
                          ? 'bg-[#948780] text-white'
                          : 'bg-[#cdc4c1] text-gray-600'
                      }`}
                    >
                      {index + 1}
                    </div>
                    <span className="hidden md:inline mx-1 md:text-base text-sm font-medium">
                      {step.label}
                    </span>
                    {/* {index < steps.length - 1 && (
                    <div className="w-8 h-0.5 bg-gray-200 mx-0.5" />
                  )} */}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Step Content with Fixed Height and Scrolling */}
          <div
            ref={scrollContainerRef}
            className="flex-1 min-h-0 px-4 md:pl-0 md:pr-4 overflow-y-auto md:max-w-[80%] w-full md:mx-auto bg-transparent md:h-[500px]"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#888 transparent',
            }}
          >
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="h-full space-y-1"
              >
                {renderStepContent()}
              </form>
            </Form>
          </div>

          {/* Fixed Footer */}
          <div className="flex-shrink-0 p-4 md:p-6">
            <div className="flex justify-between">
              <Button
                className="border-[#cdc4c1] rounded-full"
                type="button"
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                aria-label="Previous step"
              >
                <ChevronLeft className="w-4 h-4 mr-2" aria-hidden="true" />
                Previous
              </Button>

              {currentStep < steps.length - 1 ? (
                <Button
                  className="border-[#cdc4c1] rounded-full"
                  type="button"
                  onClick={handleNext}
                  aria-label="Next step"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" aria-hidden="true" />
                </Button>
              ) : (
                <Button
                  className="border-[#cdc4c1] rounded-full"
                  type="submit"
                  disabled={
                    loading ||
                    createEducationMutation.isPending ||
                    updateEducationMutation.isPending
                  }
                  onClick={form.handleSubmit(onSubmit)}
                >
                  {loading ||
                  createEducationMutation.isPending ||
                  updateEducationMutation.isPending
                    ? isEdit
                      ? 'Updating...'
                      : 'Creating...'
                    : isEdit
                      ? 'Update Education'
                      : 'Create Education'}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
